import { useEffect, useMemo, useState } from 'react';
import { cellsToGrid } from '../lib/marching-cubes';
import { gridToScene, meshToScene } from '../lib/iso-scene';
import { capByTriangles, mergeMeshes } from '../lib/iso-levels';
import { run, isAbortError } from '../lib/worker-pool';

/**
 * Iso-surfaces of a 3D inversion model (docs/TODO.md 3.8, 4.11). The cell grid is built here, the
 * marching-cubes pass runs in the worker pool (task `isosurface`, one task per level, input buffer
 * transferred, result buffers transferred back) and each mesh is mapped into the scene frame.
 *
 * `levels` is a referentially stable array of { value (ohm.m), rgb, index }; the grid holds log10
 * resistivity. `enabled` builds the grid (needed for the plane slice); `extract` runs the surface passes.
 * Returns { grid, aff, bounds, meshes: [{ ...level, mesh }], mesh (all merged, for export), capped, busy, error }.
 */
export default function useIsoSurface({ entry, origin, enabled, extract = enabled, levels }) {
  const [state, setState] = useState({ meshes: [], capped: false, busy: false, error: null });

  const built = useMemo(() => {
    if (!enabled || !entry?.cells?.length || !origin) return { grid: null, aff: null, error: null };
    try {
      const grid = cellsToGrid(entry.cells, 'resistivity');
      if (!grid) return { grid: null, aff: null, error: 'Not a 3D model (no y coordinate)' };
      const aff = gridToScene(entry.cells, grid, origin);
      return { grid, aff, error: aff ? null : 'Model has no georeference (lon/lat)' };
    } catch (e) {
      return { grid: null, aff: null, error: e.message };
    }
  }, [enabled, entry, origin]);
  const { grid, aff } = built;

  const bounds = useMemo(() => {
    if (!grid || !aff) return null;
    const { x, y, z } = grid.coords;
    const min = [Infinity, Infinity, Infinity], max = [-Infinity, -Infinity, -Infinity];
    for (const mx of [x[0], x[x.length - 1]]) for (const my of [y[0], y[y.length - 1]]) for (const md of [z[0], z[z.length - 1]]) {
      const p = [aff.a * mx + aff.b * my + aff.tx, -md, aff.c * mx + aff.d * my + aff.tz];
      for (let k = 0; k < 3; k++) { min[k] = Math.min(min[k], p[k]); max[k] = Math.max(max[k], p[k]); }
    }
    return { min, max };
  }, [grid, aff]);

  useEffect(() => {
    if (!extract || !grid || !aff || !levels?.length) {
      setState((s) => (s.meshes.length || s.busy || s.error ? { meshes: [], capped: false, busy: false, error: null } : s));
      return undefined;
    }
    let cancelled = false;
    const ctrl = new AbortController();
    const timer = setTimeout(async () => {
      setState((s) => ({ ...s, busy: true, error: null }));
      try {
        const results = await Promise.all(levels.map((lv) => {
          const values = grid.values.slice(); // the worker gets (and owns) this copy
          return run('isosurface', { values, dims: grid.dims, coords: grid.coords, iso: Math.log10(lv.value) },
            { label: 'Iso-surface', signal: ctrl.signal, transfer: [values.buffer] });
        }));
        if (cancelled) return;
        const found = results.map((res, i) => ({ ...levels[i], mesh: res.triangleCount ? meshToScene(res, aff) : null })).filter((m) => m.mesh);
        const { kept, capped } = capByTriangles(found);
        setState({ meshes: kept, capped, busy: false, error: kept.length ? null : 'No surface at the selected levels' });
      } catch (e) {
        if (!cancelled && !isAbortError(e)) setState({ meshes: [], capped: false, busy: false, error: e.message });
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(timer); ctrl.abort(); };
  }, [extract, grid, aff, levels]);

  const mesh = useMemo(() => mergeMeshes(state.meshes.map((m) => m.mesh)), [state.meshes]);
  return { grid, aff, bounds, meshes: state.meshes, mesh, capped: state.capped, busy: state.busy, error: built.error || state.error };
}
